import React, { useRef, useCallback } from 'react'
import MuxPlayer from '@mux/mux-player-react'
import { useAuth } from '../contexts/AuthContext'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

const VideoPlayer = ({
  video,
  className = '',
  autoPlay = false,
  muted = false,
  onProgress,
  onFirstPlay,
}) => {
  const { user } = useAuth()
  const { toast } = useToast()
  const lastReportedTime = useRef(0)
  const hasPlayedOnce = useRef(false)

  const handlePlay = useCallback(() => {
    if (hasPlayedOnce.current || !onFirstPlay) return
    hasPlayedOnce.current = true
    onFirstPlay()
  }, [onFirstPlay])

  const handleTimeUpdate = useCallback((e) => {
    if (!onProgress) return
    const player = e.target
    const currentTime = player.currentTime
    const duration = player.duration
    if (!duration || !isFinite(duration)) return

    // Throttle: only report every 10 seconds of playback change
    if (Math.abs(currentTime - lastReportedTime.current) < 10) return
    lastReportedTime.current = currentTime

    console.log('[VideoPlayer] Progress:', Math.round(currentTime), '/', Math.round(duration))
    onProgress(currentTime, duration)
  }, [onProgress])

  const handleEnded = useCallback((e) => {
    if (!onProgress) return
    const player = e.target
    const duration = player.duration
    if (duration && isFinite(duration)) {
      onProgress(duration, duration)
    }
  }, [onProgress])

  // Show subscription placeholder if no playback ID
  if (!video?.playbackId || video?.requiresSubscription) {
    return (
      <Card className={`bg-card border-muted ${className}`}>
        <CardContent className="p-0">
          <div className="w-full aspect-video rounded-md flex items-center justify-center text-muted-foreground flex-col gap-3">
            <div className="text-5xl opacity-30">🔒</div>
            <div className="text-base font-medium text-center">Subscription Required</div>
            <div className="text-sm opacity-80 text-center max-w-md px-6">
              This content requires an active subscription to view.
            </div>
            <Button className="mt-2" onClick={() => (window.location.href = '/auth')}>Subscribe Now</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const playbackId = video.signedPlaybackId || video.playbackId

  return (
    <div className={`relative video-player-container ${className}`}>
      <MuxPlayer
        playbackId={playbackId}
        accentColor="#49df4b"
        streamType="on-demand"
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        preload="metadata"
        startTime={video.startTime || 0}
        poster={video.thumbnail}
        metadata={{
          video_id: video.id?.toString() || 'unknown',
          video_title: video.title || 'Untitled Video',
          viewer_user_id: user?.id || 'anonymous-user',
        }}
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={(e) =>
          toast({
            title: 'Playback error',
            description: 'There was a problem loading this video.',
            variant: 'destructive',
          })
        }
        style={{ width: '100%', aspectRatio: '16/9', borderRadius: '8px' }}
      />
    </div>
  )
}

export default VideoPlayer
